import * as z from 'zod';

export const patientBaseSchema = z.object({
  first_name: z
    .string()
    .nonempty('Please enter first name.')
    .min(2, 'First must contain at-least 2 characters.'),
  middle_name: z.string().optional(),
  last_name: z
    .string()
    .nonempty('Please enter last name.')
    .min(2, 'Last name must contain at-least 2 characters.'),
  gender: z.string().nonempty('Please select a gender.'),
  dob: z.string().nonempty('Please select a date of birth.'),
  species: z.string().default('human').optional(),
  animal_owner_name: z.string().optional(),
  weight: z.string().optional(),
  note: z.string().max(250, 'Patient Notes cannot exceed 250 characters.').optional(),
  weight_recorded_date: z.string().optional(),
  height: z.number().min(0, 'Height must be positive').max(120, 'Height must be less than 120 inches').optional(),
  blood_pressure_systolic: z.union([z.number().int().min(50, 'Systolic must be at least 50').max(300, 'Systolic must be at most 300'), z.literal('')]).optional(),
  blood_pressure_diastolic: z.union([z.number().int().min(20, 'Diastolic must be at least 20').max(200, 'Diastolic must be at most 200'), z.literal('')]).optional(),
  blood_pressure_recorded_at: z.string().optional(),
  medication_allergies: z.array(z.string().max(100, 'Allergy name too long')).optional(),
  no_known_allergies: z.boolean().optional(),
  medical_history: z.string().max(5000, 'Medical history cannot exceed 5000 characters').optional(),
  preferred_contact_methods: z.object({
    email: z.boolean().optional(),
    sms: z.boolean().optional(),
    phone: z.boolean().optional(),
  }).optional(),
  emergency_contact_phone: z.string().optional(),
  insurance_carrier: z.string().max(100, 'Insurance carrier cannot exceed 100 characters').optional(),
  insurance_member_id: z.string().max(100, 'Member ID cannot exceed 100 characters').optional(),
  insurance_group_id: z.string().max(100, 'Group ID cannot exceed 100 characters').optional(),
  status: z.string().nonempty('Please select a status.'),
  practitioner_id: z.string().optional().or(z.literal('')),
  assignedClinic: z.string().nonempty('Please select clinic.'),
  contacts: z.array(
    z
      .object({
        id: z.string().optional(),
        key: z.string().optional(),
        value: z.string().optional(),
        is_default: z.boolean().optional(),
      })
      .superRefine((data, ctx) => {
        const contact = data.value || '';

        if (data.key === 'phone' || data.key === 'sms') {
          // US phone numbers in E.164 format are 12 characters: +1XXXXXXXXXX
          // But the react-phone-number-input returns 12 or 13 chars (with or without +)
          // We'll check for +1XXXXXXXXXX (12 chars) or 1XXXXXXXXXX (11 chars) or 10 digits
          // But for strictness, let's require +1XXXXXXXXXX (12 chars) or +1XXXXXXXXXX (13 chars with +)
          if (!/^\+1\d{10}$/.test(contact)) {
            ctx.addIssue({
              path: ['value'],
              code: z.ZodIssueCode.custom,
              message: 'Please enter a valid US phone number in the format +1XXXXXXXXXX',
            });
          }
        }

        if (data.key === 'email') {
          if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(contact)) {
            ctx.addIssue({
              path: ['value'],
              code: z.ZodIssueCode.custom,
              message: 'Please enter a valid email address',
            });
          }
        }

        if (data.key === 'url') {
          // URL validation matching novamed-admin but optional
          // If empty or just the protocol prefix, it's optional so we allow it
          if (!contact || contact === '' || contact === 'https://' || contact === 'http://') {
            // For optional URL, we don't add an issue if it's empty
            return;
          }

          // If there's actual content, validate it
          try {
            new URL(contact);
            // Check if there's actual domain content after the protocol
            const domainPart = contact.replace(/^https?:\/\//g, '').trim();
            // Must have at least some content that looks like a domain
            if (!domainPart || domainPart.length < 4 || !domainPart.includes('.')) {
              ctx.addIssue({
                path: ['value'],
                code: z.ZodIssueCode.custom,
                message: 'Please enter a valid website URL (e.g., example.com)',
              });
            }
          } catch (error) {
            ctx.addIssue({
              path: ['value'],
              code: z.ZodIssueCode.custom,
              message: 'Please enter a valid website URL (e.g., example.com)',
            });
          }
        }

        if (data.key === 'fax') {
          // Remove all non-digit characters for validation
          const digitsOnly = contact.replace(/\D/g, '');

          // Acceptable: 10-13 digits, may start with +, may have spaces/dashes/parentheses
          // Accept: +1XXXXXXXXXX, 1XXXXXXXXXX, or XXXXXXXXXX (US numbers)
          // But allow up to 13 digits for international
          if (
            !(
              (
                /^\+1\d{10}$/.test(contact) || // +1XXXXXXXXXX
                /^1\d{10}$/.test(contact) || // 1XXXXXXXXXX
                /^\d{10,13}$/.test(digitsOnly)
              ) // 10-13 digits, no formatting
            )
          ) {
            ctx.addIssue({
              path: ['value'],
              code: z.ZodIssueCode.custom,
              message:
                'Please enter a valid fax number (e.g., +11234567890, 1234567890, or 10-13 digits)',
            });
          }
          // Optionally, enforce max length
          if (digitsOnly.length < 10 || digitsOnly.length > 13) {
            ctx.addIssue({
              path: ['value'],
              code: z.ZodIssueCode.custom,
              message: 'Fax number must be between 10 and 13 digits long',
            });
          }
        }

        // For "others" — no validation needed
      })
  ),
  addresses: z.array(
    z.object({
      addressLine1: z
        .string()
        .nonempty('Please enter address line 1.')
        .min(4, 'Address line 1 must contain at-least 4 characters.')
        .max(255, 'Address line 1 cannot exceed 255 characters'),
      addressLine2: z.string().max(255, 'Address line 2 cannot exceed 255 characters').optional().or(z.literal('')),
      city: z.string().min(2, 'Please enter a valid city').max(50, 'City must be at most 50 characters').nonempty('Please enter a valid city'),
      state: z.string().min(2, 'Please select a state').nonempty('Please select a state'),
      postalCode: z.string().regex(/^\d{5}$/, 'Postal code must be 5 digits').nonempty('Please enter a valid postal code'),
      address_type: z.enum(['billing', 'shipping']).default('billing'),
    }).required()
  ),
  shippingAddressEnabled: z.boolean().optional(),
  shippingSameAsBilling: z.boolean().optional(),
});

export const getPatientSchema = (isGuided: boolean) => {
  if (isGuided) {
    return patientBaseSchema;
  }
  return patientBaseSchema.superRefine((data, ctx) => {
    // Validate that either allergies are selected OR "no known allergies" is checked
    const hasAllergies = data.medication_allergies && data.medication_allergies.length > 0;
    const noKnownAllergies = data.no_known_allergies === true;

    if (!hasAllergies && !noKnownAllergies) {
      ctx.addIssue({
        path: ['medication_allergies'],
        code: z.ZodIssueCode.custom,
        message: 'Please select allergies or check "No Known Allergies"',
      });
    }

    // If "no known allergies" is checked, allergies should be empty
    if (noKnownAllergies && hasAllergies) {
      ctx.addIssue({
        path: ['medication_allergies'],
        code: z.ZodIssueCode.custom,
        message: 'Cannot have allergies selected when "No Known Allergies" is checked',
      });
    }

    // Assigned Practitioner is required for non-guided clinics
    if (!data.practitioner_id || data.practitioner_id === '') {
      ctx.addIssue({
        path: ['practitioner_id'],
        code: z.ZodIssueCode.custom,
        message: 'Please select an assigned practitioner',
      });
    }
  });
};

export const patientSchema = getPatientSchema(false);

// ─────────────────────────────────────────────────────────────────────────────
// Guided "New patient" schema — used by GuidedNewPatientForm inside the
// guided enrollment modal. The practitioner only needs to provide the
// patient's email; everything else (name, gender, DOB, address, phone) is
// optional and the patient can fill it in when they open the intake link.
// ─────────────────────────────────────────────────────────────────────────────

const guidedContactItemSchema = z
  .object({
    id: z.string().optional(),
    key: z.string().optional(),
    value: z.string().optional(),
    is_default: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    const v = (data.value || '').trim();
    if (!v) return; // empty values are allowed; required-email is enforced at the array level
    if (data.key === 'email') {
      if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(v)) {
        ctx.addIssue({
          path: ['value'],
          code: z.ZodIssueCode.custom,
          message: 'Please enter a valid email address',
        });
      }
    }
    if (data.key === 'phone' || data.key === 'sms') {
      if (!/^\+1\d{10}$/.test(v)) {
        ctx.addIssue({
          path: ['value'],
          code: z.ZodIssueCode.custom,
          message: 'Please enter a valid US phone number in the format +1XXXXXXXXXX',
        });
      }
    }
  });

const guidedAddressItemSchema = z.object({
  addressLine1: z.string().max(255).optional().or(z.literal('')),
  addressLine2: z.string().max(255).optional().or(z.literal('')),
  addressLine3: z.string().max(255).optional().or(z.literal('')),
  city: z.string().max(50).optional().or(z.literal('')),
  state: z.string().optional().or(z.literal('')),
  postalCode: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine(v => !v || /^\d{5}$/.test(v), {
      message: 'Postal code must be 5 digits',
    }),
  address_type: z.enum(['billing', 'shipping']).default('billing'),
});

export const guidedNewPatientSchema = z
  .object({
    first_name: z
      .string()
      .nonempty('Please enter first name.')
      .min(2, 'First name must contain at-least 2 characters.'),
    middle_name: z.string().optional().or(z.literal('')),
    last_name: z
      .string()
      .nonempty('Please enter last name.')
      .min(2, 'Last name must contain at-least 2 characters.'),
    gender: z.string().nonempty('Please select a gender.'),
    dob: z.string().optional().or(z.literal('')),
    species: z.string().default('human').optional(),
    animal_owner_name: z.string().optional(),
    weight: z.string().optional(),
    weight_recorded_date: z.string().optional(),
    height: z.number().optional(),
    blood_pressure_systolic: z.union([z.number(), z.literal('')]).optional(),
    blood_pressure_diastolic: z.union([z.number(), z.literal('')]).optional(),
    blood_pressure_recorded_at: z.string().optional(),
    medication_allergies: z.array(z.string()).optional(),
    no_known_allergies: z.boolean().optional(),
    medical_history: z.string().optional(),
    note: z.string().max(250).optional(),
    preferred_contact_methods: z
      .object({
        email: z.boolean().optional(),
        sms: z.boolean().optional(),
        phone: z.boolean().optional(),
      })
      .optional(),
    emergency_contact_phone: z.string().optional(),
    insurance_carrier: z.string().max(100).optional(),
    insurance_member_id: z.string().max(100).optional(),
    insurance_group_id: z.string().max(100).optional(),
    status: z.string().optional().or(z.literal('')),
    practitioner_id: z.string().optional().or(z.literal('')),
    assignedClinic: z.string().nonempty('Clinic is required'),
    contacts: z.array(guidedContactItemSchema),
    addresses: z.array(guidedAddressItemSchema).optional(),
    shippingAddressEnabled: z.boolean().optional(),
    shippingSameAsBilling: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    // Required: email AND phone. Both are needed for MDI submission
    // downstream — when phone is missing the GLP-1 quiz auto-route silently
    // skips at validateMDIReadiness with the order stranded at
    // intake_completed, so block here instead of failing later.
    const contacts = data.contacts || [];
    const emailIdx = contacts.findIndex(c => c.key === 'email');
    const hasValidEmail = contacts.some(
      c =>
        c.key === 'email' &&
        !!c.value &&
        /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(c.value.trim()),
    );
    if (!hasValidEmail) {
      ctx.addIssue({
        path: ['contacts', emailIdx >= 0 ? emailIdx : 0, 'value'],
        code: z.ZodIssueCode.custom,
        message: "Please enter the patient's email address",
      });
    }

    const phoneIdx = contacts.findIndex(c => c.key === 'phone');
    const hasValidPhone = contacts.some(
      c =>
        c.key === 'phone'
        && !!c.value
        && /^\+1\d{10}$/.test(c.value.trim()),
    );
    if (!hasValidPhone) {
      ctx.addIssue({
        path: ['contacts', phoneIdx >= 0 ? phoneIdx : 0, 'value'],
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid US phone number in the format +1XXXXXXXXXX',
      });
    }
  });
